/**
 * Ultra-Modern Header Component
 * World-Class SaaS ERP Platform Design
 * Theme support added
 */

import { useState, useMemo, useRef, useEffect } from 'react';
import {
  MdNotifications,
  MdChatBubble,
  MdKeyboardArrowDown,
  MdPerson,
  MdLibraryBooks,
  MdExitToApp,
  MdMenu,
  MdSearch,
  MdLock,
  MdTranslate,
  MdAccessTime,
  MdCalendarToday,
  MdRefresh,
  MdClose,
} from 'react-icons/md';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { ThemeToggle } from '../ThemeToggle';
import { MessageDropdown } from './MessageDropdown';
import { searchNavigation } from '../../utils/searchNavigation';
import { authenticatedFetch } from '../../lib/api';
import { useNotificationPush } from '../../lib/useNotificationPush';

interface HeaderProps {
  sidebarCollapsed?: boolean;
  onMenuClick?: () => void;
}

interface PortalNotification {
  _id: string;
  title: string;
  message: string;
  category?: string;
  link?: string;
  metadata?: { eventKey?: string; ticketId?: string };
  createdAt: string;
  isRead?: boolean;
}

const resolveNotificationLink = (notification: PortalNotification) => {
  if (notification.link) return notification.link;
  if (notification.metadata?.ticketId) return `/support/${notification.metadata.ticketId}`;
  if (notification.metadata?.eventKey?.includes('onboarding')) return '/onboarding-data';
  if (notification.metadata?.eventKey?.includes('support')) return '/support';
  return null;
};

export function Header({ sidebarCollapsed = false, onMenuClick }: HeaderProps) {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  const searchResults = useMemo(() => searchNavigation(searchQuery), [searchQuery]);

  const navigateToSearchResult = (path: string) => {
    navigate(path);
    setSearchQuery('');
    setShowSearchResults(false);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    const results = searchNavigation(q);
    if (results.length > 0) {
      navigateToSearchResult(results[0].path);
      return;
    }
    navigate(`/dashboard/admin/rfqs?search=${encodeURIComponent(q)}`);
    setSearchQuery('');
    setShowSearchResults(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const notificationsQueryKey = ['admin-notifications', activeCategory] as const;

  const notificationPush = useNotificationPush({
    enabled: Boolean(user?._id && user?.organizationId),
    userId: user?._id,
    organizationId:
      typeof user?.organizationId === 'object' && user?.organizationId !== null
        ? String((user.organizationId as { _id?: string })._id || user.organizationId)
        : user?.organizationId,
    portalType: 'admin',
    onNotification: () => {
      // Force an immediate refetch (invalidate alone can feel "stuck" with staleTime)
      void queryClient.invalidateQueries({ queryKey: ['admin-notifications'] });
      void queryClient.refetchQueries({ queryKey: ['admin-notifications'] });
    },
  });

  const {
    data: notificationsPayload,
    isFetching: isLoadingNotifications,
    error: notificationsQueryError,
  } = useQuery({
    queryKey: notificationsQueryKey,
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('limit', '15');
      if (activeCategory !== 'all') params.append('category', activeCategory);
      const response = await authenticatedFetch(`/api/v1/admin/notifications?${params.toString()}`);
      const result = await response.json();
      if (!result?.success) {
        throw new Error(result?.error || 'Failed to load notifications');
      }
      return {
        notifications: Array.isArray(result.data) ? (result.data as PortalNotification[]) : [],
        unreadCount: result?.meta?.unreadCount || 0,
      };
    },
    enabled: Boolean(user?._id),
    // Keep a poll fallback even while WS is connected (vendor always polls).
    // Previously connected=true disabled polling — if WS never delivered, admin went silent.
    refetchInterval: notificationPush.connected ? 45_000 : 15_000,
    refetchIntervalInBackground: false,
    staleTime: 10_000,
  });

  const notifications = notificationsPayload?.notifications ?? [];
  const unreadCount = notificationsPayload?.unreadCount ?? 0;
  const notificationsError = notificationsQueryError
    ? notificationsQueryError instanceof Error
      ? notificationsQueryError.message
      : 'Failed to load notifications'
    : null;

  const markNotificationRead = async (notificationId: string) => {
    try {
      await authenticatedFetch(`/api/v1/admin/notifications/${notificationId}/read`, { method: 'POST' });
      queryClient.setQueryData(notificationsQueryKey, (old: typeof notificationsPayload) => {
        if (!old) return old;
        return {
          notifications: old.notifications.map((n) =>
            n._id === notificationId ? { ...n, isRead: true } : n
          ),
          unreadCount: Math.max(0, old.unreadCount - 1),
        };
      });
    } catch {
      // best-effort
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await authenticatedFetch('/api/v1/admin/notifications/read-all', { method: 'POST' });
      queryClient.setQueryData(notificationsQueryKey, (old: typeof notificationsPayload) => {
        if (!old) return old;
        return {
          notifications: old.notifications.map((n) => ({ ...n, isRead: true })),
          unreadCount: 0,
        };
      });
    } catch {
      // best-effort
    }
  };

  const handleLogout = async () => {
    setShowUserMenu(false);
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login', { replace: true });
    }
  };

  return (
    <header
      className={cn(
        'app-top-header fixed top-0 right-0 z-50 h-16 border-b border-[hsl(var(--border))] bg-[hsl(var(--background))] transition-[left] duration-300',
        sidebarCollapsed ? 'left-20' : 'left-72'
      )}
    >
      <div className="flex items-center justify-between h-full px-6">
        {/* Left Section */}
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 rounded-lg hover:bg-[hsl(var(--muted))] transition-colors text-[hsl(var(--muted-foreground))]"
            aria-label={t('Toggle menu')}
          >
            <MdMenu className="w-5 h-5" />
          </button>

          {/* Search Bar */}
          <div className="hidden md:flex items-center flex-1 max-w-xl ml-4 relative" ref={searchRef}>
            <form onSubmit={handleSearchSubmit} className="flex items-center flex-1 bg-[hsl(var(--secondary))] border border-[hsl(var(--border))] rounded-full overflow-hidden transition-all focus-within:border-[hsl(var(--primary))] dark:focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-[hsl(var(--primary))]/20">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowSearchResults(true);
                }}
                onFocus={() => setShowSearchResults(true)}
                placeholder={t('Search modules (RFQs, users, licenses...)')}
                className="flex-1 px-5 py-2 bg-transparent border-none outline-none text-[13px] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]"
              />
              <div className="w-px h-5 bg-[hsl(var(--border))]" />
              <button type="submit" className="px-4 py-2 text-[#0088cc] cursor-pointer hover:bg-[hsl(var(--muted))] transition-colors" aria-label={t('Search')}>
                <MdSearch className="w-5 h-5" />
              </button>
            </form>
            {showSearchResults && searchQuery.trim() && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl overflow-hidden z-[200] shadow-lg">
                {searchResults.length > 0 ? (
                  searchResults.map((result) => (
                    <button
                      key={result.path}
                      type="button"
                      onClick={() => navigateToSearchResult(result.path)}
                      className="w-full text-left px-4 py-3 hover:bg-[#0088cc]/10 transition-colors border-b border-[hsl(var(--border))] last:border-b-0"
                    >
                      <p className="text-sm font-semibold text-[hsl(var(--foreground))]">{t(result.label)}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">{result.path}</p>
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-3 text-sm text-[hsl(var(--muted-foreground))]">
                    {t('No matching modules. Press Enter to search RFQs.')}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-2">
          {/* Refresh Button */}
          <button
            onClick={() => window.location.reload()}
            className="p-2 rounded-lg hover:bg-[hsl(var(--muted))] transition-colors text-[hsl(var(--muted-foreground))]"
            aria-label={t('Refresh page')}
            title={t('Refresh page')}
          >
            <MdRefresh className="w-5 h-5" />
          </button>

          {/* Theme Toggle */}
          <div className="text-[hsl(var(--muted-foreground))]">
            <ThemeToggle />
          </div>

          {/* Messages */}
          <div className="relative">
            <button
              onClick={() => {
                setShowMessages(!showMessages);
                setShowNotifications(false);
                setShowUserMenu(false);
              }}
              className={cn(
                "p-2 rounded-lg transition-colors text-[#5c69ff]",
                showMessages ? "bg-[hsl(var(--muted))]" : "hover:bg-[hsl(var(--muted))]"
              )}
              aria-label={t('Messages')}
            >
              <MdChatBubble className="w-5 h-5" />
            </button>

            <MessageDropdown
              isOpen={showMessages}
              onClose={() => setShowMessages(false)}
              unreadCount={0}
            />
          </div>

          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => {
                setShowNotifications(!showNotifications);
                setShowMessages(false);
                setShowUserMenu(false);
              }}
              className="relative p-2 rounded-lg hover:bg-[hsl(var(--muted))] transition-colors text-[#4fd1c5]"
              aria-label={t('Notifications')}
            >
              <MdNotifications className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-[#0088cc] text-white rounded-full flex items-center justify-center border border-[hsl(var(--background))] shadow-sm">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <>
                <div
                  className="fixed inset-0 bg-black/20 z-40 transition-opacity notifications-backdrop"
                  onClick={() => setShowNotifications(false)}
                />

                <div className="fixed top-0 right-0 h-screen w-full sm:w-[500px] bg-[hsl(var(--card))] shadow-[-8px_0_32px_rgba(0,0,0,0.1)] z-50 transform flex flex-col transition-transform duration-300 notifications-drawer">
                  <div className="flex items-center justify-between px-4 py-3 bg-[hsl(var(--muted))] border-b border-[hsl(var(--border))]">
                    <div className="flex items-center gap-2">
                      <MdNotifications className="w-5 h-5 text-[hsl(var(--foreground))]" />
                      <h3 className="text-[15px] font-bold text-[hsl(var(--foreground))]">{t('Latest Notifications')}</h3>
                      <span className="text-[11px] text-[hsl(var(--muted-foreground))] font-medium">(* last 60d)</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setShowNotifications(false)} className="p-1.5 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--secondary))] rounded transition-colors" title={t('Close')}>
                        <MdClose className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 px-4 py-2 bg-[hsl(var(--card))] border-b border-[hsl(var(--border))] overflow-x-auto shrink-0">
                    <button onClick={() => setActiveCategory('all')} className={cn("flex items-center gap-2 px-3 py-1.5 border rounded text-sm font-medium transition-colors shrink-0", activeCategory === 'all' ? "bg-[hsl(var(--secondary))] border-[hsl(var(--border))] text-[hsl(var(--foreground))] font-semibold" : "bg-transparent border-transparent text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--secondary))]/50")}>{t('All')}</button>
                    <button onClick={() => setActiveCategory('onboarding')} className={cn("flex items-center gap-2 px-3 py-1.5 border rounded text-sm font-medium transition-colors shrink-0", activeCategory === 'onboarding' ? "bg-[hsl(var(--secondary))] border-[hsl(var(--border))] text-[hsl(var(--foreground))] font-semibold" : "bg-transparent border-transparent text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--secondary))]/50")}>{t('Onboarding')}</button>
                    <button onClick={() => setActiveCategory('support')} className={cn("flex items-center gap-2 px-3 py-1.5 border rounded text-sm font-medium transition-colors shrink-0", activeCategory === 'support' ? "bg-[hsl(var(--secondary))] border-[hsl(var(--border))] text-[hsl(var(--foreground))] font-semibold" : "bg-transparent border-transparent text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--secondary))]/50")}>{t('Support')}</button>
                  </div>

                  <div className="flex-1 overflow-y-auto bg-[hsl(var(--card))]">
                    {isLoadingNotifications ? (
                      <div className="p-8 text-center text-sm text-[hsl(var(--muted-foreground))] font-medium">{t('Loading notifications...')}</div>
                    ) : notificationsError ? (
                      <div className="p-8 text-center text-sm text-red-500 font-medium">{notificationsError}</div>
                    ) : notifications.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full p-8 text-center text-[hsl(var(--muted-foreground))]">
                        <MdNotifications className="w-12 h-12 text-[hsl(var(--muted-foreground))]/20 mb-4" />
                        <p className="font-medium text-[hsl(var(--foreground))]">{t('No new notifications')}</p>
                        <p className="text-sm mt-1 text-[hsl(var(--muted-foreground))]">{t("You're all caught up.")}</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-[hsl(var(--border))]">
                        {notifications.map((notification) => (
                          <button
                            key={notification._id}
                            onClick={() => {
                              if (!notification.isRead) markNotificationRead(notification._id);
                              const target = resolveNotificationLink(notification);
                              if (target) {
                                navigate(target);
                                setShowNotifications(false);
                              }
                            }}
                            className={cn(
                              'w-full text-left px-5 py-4 hover:bg-[hsl(var(--accent))] transition-colors border-l-4',
                              notification.isRead ? 'border-transparent' : 'border-[#0088cc] bg-[#0088cc]/10',
                              resolveNotificationLink(notification) ? 'cursor-pointer' : 'cursor-default'
                            )}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <p className={cn('text-sm', notification.isRead ? 'font-medium' : 'font-bold')}>{notification.title}</p>
                                <p className="text-[13px] text-[hsl(var(--muted-foreground))] line-clamp-2 mt-1">{notification.message}</p>
                              </div>
                              {!notification.isRead && <span className="mt-1.5 w-2 h-2 rounded-full bg-[#0088cc] shrink-0" />}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 border-t border-[hsl(var(--border))] bg-[hsl(var(--card))] shrink-0">
                    <button onClick={() => setShowNotifications(false)} className="py-4 text-[13px] font-semibold text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--secondary))] transition-colors border-r border-[hsl(var(--border))]">{t('Close')}</button>
                    <button onClick={markAllNotificationsRead} disabled={unreadCount === 0} className="py-4 text-[13px] font-bold text-[#0088cc] hover:bg-[#0088cc]/10 transition-colors disabled:opacity-50">{t('Mark all as read')}</button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[hsl(var(--muted))] transition-colors"
            >
              <div className="w-8 h-8 rounded-lg bg-[#0088cc] flex items-center justify-center text-white font-bold text-[13px] shadow-sm">
                {user?.firstName?.[0] || user?.email?.[0] || 'A'}
              </div>
              <div className="hidden md:flex flex-col items-start text-left">
                <span className="text-sm font-semibold text-[hsl(var(--foreground))]">
                  {user?.firstName && user?.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : user?.email || 'User'}
                </span>
                <span className="text-xs text-[hsl(var(--muted-foreground))]">{user?.role || 'Admin'}</span>
              </div>
              <MdKeyboardArrowDown
                className={cn(
                  'w-4 h-4 text-[hsl(var(--muted-foreground))] transition-transform hidden md:block',
                  showUserMenu && 'rotate-180'
                )}
              />
            </button>

            {showUserMenu && (
              <>
                <div
                  className="fixed inset-0 z-[9998]"
                  onClick={() => setShowUserMenu(false)}
                />
                <div className="absolute right-0 top-full mt-2 w-64 bg-[hsl(var(--card))] rounded-xl shadow-xl border border-[hsl(var(--border))] overflow-hidden z-[9999]">
                  <div className="p-4 bg-[hsl(var(--secondary))] border-b border-[hsl(var(--border))]">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#0088cc] flex items-center justify-center text-white font-semibold">
                        {user?.firstName?.[0] || user?.email?.[0] || 'A'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[hsl(var(--foreground))] truncate">
                          {user?.firstName && user?.lastName
                            ? `${user.firstName} ${user.lastName}`
                            : user?.email || 'User'}
                        </p>
                        <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">{user?.email}</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-2">
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate('/profile');
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-[hsl(var(--secondary))] transition-colors text-[hsl(var(--foreground))] text-sm"
                    >
                      <MdPerson className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                      <span>{t('Profile')}</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate('/profile?tab=password');
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-[hsl(var(--secondary))] transition-colors text-[hsl(var(--foreground))] text-sm"
                    >
                      <MdLock className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                      <span>{t('Change/Reset Password')}</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate('/profile?tab=language');
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-[hsl(var(--secondary))] transition-colors text-[hsl(var(--foreground))] text-sm"
                    >
                      <MdTranslate className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                      <span>{t('Language Selection')}</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate('/profile?tab=timezone');
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-[hsl(var(--secondary))] transition-colors text-[hsl(var(--foreground))] text-sm"
                    >
                      <MdAccessTime className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                      <span>{t('Time Zone')}</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate('/profile?tab=date-format');
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-[hsl(var(--secondary))] transition-colors text-[hsl(var(--foreground))] text-sm"
                    >
                      <MdCalendarToday className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                      <span>{t('Date and Time Format')}</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate('/support/tickets');
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-[hsl(var(--secondary))] transition-colors text-[hsl(var(--foreground))] text-sm"
                    >
                      <MdLibraryBooks className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                      <span>{t('Help & Docs')}</span>
                    </button>
                    <div className="my-1 border-t border-[hsl(var(--border))]" />
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-red-500/10 transition-colors text-red-500 text-sm"
                    >
                      <MdExitToApp className="w-4 h-4" />
                      <span>{t('Logout')}</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

